import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { AlertDialog } from '@zorro/design-system/v2/Dialog';
import { Stack } from '@zorro/web/design-system';
import { useBatchCallEndpoint, useIdempotencyKey } from '@zorro/web/ui-utils';
import { useState } from 'react';

import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

interface ResetElectionDialogProps {
  isOpen: boolean;
  recordsForActions: RecordForActions[];
  hasNonWaivedRecords: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Mounted only while open so the checkbox and batch errors start fresh each time.
export const ResetElectionDialog = ({
  isOpen,
  ...props
}: ResetElectionDialogProps) =>
  isOpen ? <ResetElectionAlert {...props} /> : null;

const ResetElectionAlert = ({
  recordsForActions,
  hasNonWaivedRecords,
  onClose,
  onSuccess,
}: Omit<ResetElectionDialogProps, 'isOpen'>) => {
  const idempotencyKey = useIdempotencyKey();
  const [shouldReturnBenefitsToCart, setShouldReturnBenefitsToCart] =
    useState(hasNonWaivedRecords);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    executeBatchCall: batchResetBenefitElections,
    errors: batchResetBenefitElectionsErrors,
  } = useBatchCallEndpoint({
    singularItemName: 'election',
    action: 'reset',
    methodName: 'onboardingPeriodsControllerResetBenefitsElection',
  });

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { errors } = await batchResetBenefitElections(
        recordsForActions.map(({ fullName, onboardingPeriodId }) => {
          if (!onboardingPeriodId) {
            throw new Error(`${fullName} doesn't have an onboarding period`);
          }
          return {
            key: fullName,
            params: [
              onboardingPeriodId,
              idempotencyKey.getFor(onboardingPeriodId),
              hasNonWaivedRecords && shouldReturnBenefitsToCart,
            ],
          };
        })
      );
      if (errors.length === 0) {
        onSuccess();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBulk = recordsForActions.length > 1;
  const hasErrors = isBulk && batchResetBenefitElectionsErrors.length > 0;

  return (
    <AlertDialog
      isOpen
      onClose={onClose}
      variant="negative"
      title={hasNonWaivedRecords ? 'Reset election?' : 'Undo waiver?'}
      description={
        hasNonWaivedRecords
          ? 'All current plan elections and enrollment data will be deleted'
          : 'This will remove the waiver and return the enrollment to its previous state'
      }
      primaryLabel={hasNonWaivedRecords ? 'Reset election' : 'Undo waiver'}
      onPrimaryAction={onSubmit}
      isPrimaryLoading={isSubmitting}
      secondaryLabel="Cancel"
      onSecondaryAction={onClose}
    >
      {(hasNonWaivedRecords || hasErrors) && (
        <Stack>
          {hasNonWaivedRecords && (
            <Checkbox
              label="Preserve plan selections in cart for resubmission"
              checked={shouldReturnBenefitsToCart}
              onChange={(event) =>
                setShouldReturnBenefitsToCart(event.currentTarget.checked)
              }
            />
          )}
          <ErrorsTable
            tableTitle="Employee name"
            errors={batchResetBenefitElectionsErrors}
            isBulk={isBulk}
          />
        </Stack>
      )}
    </AlertDialog>
  );
};
